import React, { useState, useEffect } from 'react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Modal, ModalHeader, ModalTitle, ModalContent, ModalFooter } from '../ui/Modal';
import ConfirmationDialog from '../ui/ConfirmationDialog';
import { DENSITY_CONFIG } from './AdvancedTable';
import { useTheme } from '../../contexts/ThemeContext';
import { getTableActionsConfig } from '../../config/appConfig';
import { FolderPlusIcon, XIcon, CopyIcon, RefreshIcon, DocumentTextIcon, CheckIcon, PencilIcon, TrashIcon } from '../ui/Icons';

const TablePresets = ({ 
  // Column related
  visibleColumns = {},
  onApplyVisibleColumns,
  
  // Table settings
  tableDensity = 'normal',
  onApplyDensity,
  
  // Column configuration (from AdvancedTable)
  columnWidths = {},
  columnOrder = [],
  sortConfig = [],
  onApplyTableConfig,
  
  // Component props
  tableId = 'default-table',
  entityName = 'Table',
  isOpen,
  onClose,
  
  // Default preset loading
  onLoadDefaultPreset
}) => {
  const { theme } = useTheme();
  
  // Get table actions configuration
  const actionsVisibility = getTableActionsConfig('visibility');
  const showTooltips = getTableActionsConfig('showTooltips');
  const buttonSize = getTableActionsConfig('buttonSize');
  const spacing = getTableActionsConfig('spacing');
  const [presets, setPresets] = useState([]);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [presetName, setPresetName] = useState('');
  const [presetDescription, setPresetDescription] = useState('');
  const [editingPreset, setEditingPreset] = useState(null);
  const [activeTab, setActiveTab] = useState('customize');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [presetToDelete, setPresetToDelete] = useState(null);

  // Load presets from localStorage
  useEffect(() => {
    const savedPresets = localStorage.getItem(`table-presets-${tableId}`);
    if (savedPresets) {
      try {
        setPresets(JSON.parse(savedPresets));
      } catch (error) {
        console.warn('Failed to load table presets:', error);
      }
    }
  }, [tableId]);

  // Save presets to localStorage
  const savePresetsToStorage = (newPresets) => {
    localStorage.setItem(`table-presets-${tableId}`, JSON.stringify(newPresets));
    setPresets(newPresets);
  };

  // Get/Set default preset
  const getDefaultPresetId = () => {
    return localStorage.getItem(`default-preset-${tableId}`);
  };

  const setDefaultPreset = (presetId) => {
    if (presetId) {
      localStorage.setItem(`default-preset-${tableId}`, presetId);
    } else {
      localStorage.removeItem(`default-preset-${tableId}`);
    }
  };

  // Get current table configuration (excluding data filters)
  const getCurrentConfig = () => ({
    visibleColumns: visibleColumns,
    tableDensity: tableDensity,
    columnWidths: columnWidths,
    columnOrder: columnOrder,
    sortConfig: sortConfig
  });

  // Apply table configuration (excluding data filters)
  const applyCompleteConfig = (config) => {
    if (config.visibleColumns && onApplyVisibleColumns) {
      onApplyVisibleColumns(config.visibleColumns);
    }
    if (config.tableDensity && onApplyDensity) {
      onApplyDensity(config.tableDensity);
    }
    if (onApplyTableConfig) {
      onApplyTableConfig({
        columnWidths: config.columnWidths || {},
        columnOrder: config.columnOrder || [],
        sortConfig: config.sortConfig || []
      });
    }
  };

  // Save current configuration as a new preset
  const handleSavePreset = () => {
    if (!presetName.trim()) return;

    const newPreset = {
      id: Date.now().toString(),
      name: presetName.trim(),
      description: presetDescription.trim(),
      config: getCurrentConfig(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    const newPresets = [...presets, newPreset];
    savePresetsToStorage(newPresets);
    
    setPresetName('');
    setPresetDescription('');
    setShowSaveModal(false);
  };

  // Update existing preset
  const handleUpdatePreset = () => {
    if (!presetName.trim() || !editingPreset) return;

    const updatedPresets = presets.map(preset => 
      preset.id === editingPreset.id 
        ? {
            ...preset,
            name: presetName.trim(),
            description: presetDescription.trim(),
            config: getCurrentConfig(),
            updatedAt: new Date().toISOString()
          }
        : preset
    );

    savePresetsToStorage(updatedPresets);
    
    setPresetName('');
    setPresetDescription('');
    setEditingPreset(null);
    setShowSaveModal(false);
  };

  // Show delete confirmation
  const handleDeletePreset = (preset) => {
    setPresetToDelete(preset);
    setShowDeleteConfirm(true);
  };

  // Confirm delete preset
  const confirmDeletePreset = () => {
    if (presetToDelete) {
      // If deleting the default preset, remove it as default first
      if (getDefaultPresetId() === presetToDelete.id) {
        setDefaultPreset(null);
      }
      
      const newPresets = presets.filter(preset => preset.id !== presetToDelete.id);
      savePresetsToStorage(newPresets);
    }
    
    setShowDeleteConfirm(false);
    setPresetToDelete(null);
  };

  // Cancel delete
  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setPresetToDelete(null);
  };

  // Apply a preset
  const handleApplyPreset = (preset) => {
    applyCompleteConfig(preset.config);
  };

  // Set preset as default
  const handleSetAsDefault = (presetId) => {
    setDefaultPreset(presetId);
  };

  // Remove default preset
  const handleRemoveDefault = () => {
    setDefaultPreset(null);
  };





  // Start editing a preset
  const startEditPreset = (preset) => {
    setEditingPreset(preset);
    setPresetName(preset.name);
    setPresetDescription(preset.description || '');
    setShowSaveModal(true);
  };

  // Get table configuration summary for display (excluding data filters)
  const getConfigSummary = (config) => {
    const parts = [];
    
    // Count visible columns
    const totalColumns = Object.keys(config.visibleColumns || {}).length;
    const visibleCount = Object.values(config.visibleColumns || {}).filter(Boolean).length;
    const hiddenCount = totalColumns - visibleCount;
    
    if (totalColumns > 0) {
      if (hiddenCount === 0) {
        parts.push(`all ${visibleCount} columns visible`);
      } else {
        parts.push(`${visibleCount}/${totalColumns} columns visible`);
      }
    }
    
    // Table density
    if (config.tableDensity && config.tableDensity !== 'normal') {
      parts.push(`${config.tableDensity} density`);
    } else if (config.tableDensity === 'normal') {
      parts.push('normal density');
    }
    
    // Sort configuration with details
    if (config.sortConfig && config.sortConfig.length > 0) {
      const sortDescriptions = config.sortConfig
        .sort((a, b) => (a.priority || 1) - (b.priority || 1))
        .map(sort => `${sort.key} ${sort.direction}`)
        .slice(0, 2); // Show first 2 sorts
      
      if (config.sortConfig.length === 1) {
        parts.push(`sorted by ${sortDescriptions[0]}`);
      } else {
        parts.push(`${config.sortConfig.length} sorts: ${sortDescriptions.join(', ')}${config.sortConfig.length > 2 ? '...' : ''}`);
      }
    }
    
    // Column customizations
    const customizations = [];
    if (config.columnWidths && Object.keys(config.columnWidths).length > 0) {
      customizations.push('sizing');
    }
    if (config.columnOrder && config.columnOrder.length > 0) {
      customizations.push('order');
    }
    
    if (customizations.length > 0) {
      parts.push(`custom ${customizations.join(' & ')}`);
    }
    
    return parts.length > 0 ? parts.join(', ') : 'Default configuration';
  };

  // Check if current configuration matches a preset
  const isPresetActive = (preset) => {
    return JSON.stringify(preset.config) === JSON.stringify(getCurrentConfig());
  };

  // Reset to default table configuration (not affecting data filters)
  const handleResetToDefaults = () => {
    // Set all columns to visible by default
    const defaultVisibleColumns = Object.keys(visibleColumns).reduce((acc, key) => ({
      ...acc,
      [key]: true
    }), {});

    applyCompleteConfig({
      visibleColumns: defaultVisibleColumns,
      tableDensity: 'normal',
      columnWidths: {},
      columnOrder: [],
      sortConfig: []
    });
  };

  const hasCustomizations = () => {
    const config = getCurrentConfig();
    const hasHiddenColumns = Object.values(config.visibleColumns).some(v => v === false);
    const hasCustomDensity = config.tableDensity !== 'normal';
    const hasCustomWidths = Object.keys(config.columnWidths).length > 0;
    const hasCustomOrder = config.columnOrder.length > 0;
    const hasCustomSort = config.sortConfig.length > 0;
    
    console.log('TablePresets hasCustomizations check (table only):', {
      hasHiddenColumns,
      hasCustomDensity,
      hasCustomWidths,
      hasCustomOrder,
      hasCustomSort,
      totalCustomizations: hasHiddenColumns || hasCustomDensity || hasCustomWidths || hasCustomOrder || hasCustomSort
    });
    
    return (
      hasHiddenColumns ||
      hasCustomDensity ||
      hasCustomWidths ||
      hasCustomOrder ||
      hasCustomSort
    );
  };

  if (!isOpen) return null;

  return (
    <div className={`absolute top-full right-0 mt-1 ${theme.colors.surface} ${theme.border.light} border rounded-xl ${theme.shadow.lg} min-w-96 max-w-2xl z-50 backdrop-blur-md overflow-hidden`}>
      <div className="p-4">
        {/* Header */}
        <div className={`flex items-center justify-between mb-4 pb-3 border-b ${theme.border.light}`}>
          <h3 className={`font-bold text-sm ${theme.text.primary} flex items-center`}>
            <FolderPlusIcon className={`w-4 h-4 mr-2 ${theme.text.secondary}`} />
            {entityName} Presets
          </h3>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className={`h-8 w-8 p-0 ${theme.text.secondary} hover:${theme.colors.background} hover:${theme.text.primary} rounded-lg transition-colors`}
            title="Close"
          >
            <XIcon className="w-4 h-4" />
          </Button>
        </div>

        {/* Tab Navigation */}
        <div className="flex mb-4 border-b border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setActiveTab('customize')}
            className={cn(
              "px-3 py-2 text-sm font-medium border-b-2 transition-colors",
              activeTab === 'customize'
                ? "border-blue-500 text-blue-600 dark:text-blue-400"
                : "border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
            )}
          >
            Customize
          </button>
          <button
            onClick={() => setActiveTab('presets')}
            className={cn(
              "px-3 py-2 text-sm font-medium border-b-2 transition-colors",
              activeTab === 'presets'
                ? "border-blue-500 text-blue-600 dark:text-blue-400"
                : "border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
            )}
          >
            Saved Presets
            {presets.length > 0 && (
              <span className="ml-1 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-blue-600 bg-blue-100 rounded-full dark:bg-blue-900/20 dark:text-blue-400">
                {presets.length}
              </span>
            )}
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'customize' && (
          <div>
            {/* Quick Actions */}
            <div className="flex items-center gap-2 mb-4">
              {hasCustomizations() && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowSaveModal(true)}
                  className="text-xs"
                >
                  <CopyIcon className="w-3 h-3 mr-1" />
                  Save Current
                </Button>
              )}
              
              <Button
                variant="ghost"
                size="sm"
                onClick={handleResetToDefaults}
                className="text-xs"
              >
                <RefreshIcon className="w-3 h-3 mr-1" />
                Reset All
              </Button>
            </div>

        {/* Table Density Settings */}
        <div className="mb-4 p-3 border border-gray-200 dark:border-gray-700 rounded-lg">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-medium text-sm">Table Density</h4>
            <span className="text-xs text-gray-500 dark:text-gray-400 capitalize">
              {tableDensity}
            </span>
          </div>
          <div className="space-y-2">
            {Object.entries(DENSITY_CONFIG).map(([key, config]) => (
              <label key={key} className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  name="density"
                  value={key}
                  checked={tableDensity === key}
                  onChange={() => onApplyDensity(key)}
                  className="text-blue-600 focus:ring-blue-500"
                />
                <div className="flex-1">
                  <div className="text-sm font-medium capitalize">{key}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {config.description}
                  </div>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Column Visibility Controls */}
        <div className="mb-4 p-3 border border-gray-200 dark:border-gray-700 rounded-lg">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-medium text-sm">Visible Columns</h4>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {Object.values(visibleColumns).filter(Boolean).length} of {Object.keys(visibleColumns).length}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  const allVisible = Object.keys(visibleColumns).reduce((acc, key) => ({ ...acc, [key]: true }), {});
                  onApplyVisibleColumns(allVisible);
                }}
                className="text-xs"
              >
                Show All
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(visibleColumns).map(([columnKey, isVisible]) => (
              <label key={columnKey} className="flex items-center space-x-2 cursor-pointer text-sm">
                <input
                  type="checkbox"
                  checked={isVisible}
                  onChange={(e) => {
                    const newVisibleColumns = {
                      ...visibleColumns,
                      [columnKey]: e.target.checked
                    };
                    onApplyVisibleColumns(newVisibleColumns);
                  }}
                  className="text-blue-600 focus:ring-blue-500 rounded"
                />
                <span className="truncate capitalize">
                  {columnKey.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Current Sort Configuration */}
        {sortConfig && sortConfig.length > 0 && (
          <div className="mb-4 p-3 border border-gray-200 dark:border-gray-700 rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-sm">Current Sort</h4>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onApplyTableConfig({ sortConfig: [] })}
                className="text-xs text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
              >
                Clear Sort
              </Button>
            </div>
            <div className="space-y-1">
              {sortConfig
                .sort((a, b) => (a.priority || 1) - (b.priority || 1))
                .map((sort, index) => (
                  <div key={sort.key} className="flex items-center justify-between text-sm">
                    <span className="flex items-center">
                      <span className="inline-flex items-center justify-center w-4 h-4 bg-gray-100 dark:bg-gray-700 rounded-full text-xs mr-2">
                        {sort.priority || index + 1}
                      </span>
                      <span className="font-medium">{sort.key}</span>
                    </span>
                    <span className="flex items-center text-gray-500 dark:text-gray-400">
                      {sort.direction === 'asc' ? '↑' : '↓'}
                      <span className="ml-1 capitalize">{sort.direction}</span>
                    </span>
                  </div>
                ))}
            </div>
          </div>
        )}

            {/* Instructions */}
            <div className="text-xs text-gray-500 dark:text-gray-400 border-t pt-3">
              <p>• Table Layout Customization:</p>
              <p>• Click column headers to sort</p>
              <p>• Ctrl/Cmd + click for multi-column sort</p>
              <p>• Drag column borders to resize</p>
              <p>• Drag column headers to reorder</p>
              <p>• Use checkboxes to show/hide columns</p>
            </div>
          </div>
        )}

        {activeTab === 'presets' && (
          <div>
            {/* Quick Save Action */}
            {hasCustomizations() && (
              <div className="mb-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowSaveModal(true)}
                  className="text-xs w-full"
                >
                  <CopyIcon className="w-3 h-3 mr-1" />
                  Save Current Configuration
                </Button>
              </div>
            )}

            {/* Saved Presets */}
            <div className="space-y-3 mb-4 max-h-80 overflow-y-auto scrollbar-thin">
          {presets.length === 0 ? (
            <div className="text-center py-6 text-gray-500 dark:text-gray-400">
              <svg className="w-8 h-8 mx-auto mb-2 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
              <p className="text-sm">No saved presets</p>
              <p className="text-xs">Customize the table and save presets</p>
            </div>
          ) : (
            presets.map((preset) => {
              const isDefault = getDefaultPresetId() === preset.id;
              return (
                <div
                  key={preset.id}
                  className={cn(
                    `p-4 border rounded-xl transition-all duration-200 group ${theme.shadow.sm} hover:${theme.shadow.default}`,
                    isPresetActive(preset)
                      ? `${theme.colors.primary} ${theme.text.inverse} ${theme.shadow.lg} scale-[1.02]`
                      : isDefault
                      ? `${theme.colors.success} ${theme.text.inverse} ${theme.shadow.md}`
                      : `${theme.colors.surface} ${theme.border.light} hover:${theme.colors.background} hover:scale-[1.01]`
                  )}
                >
                  {/* Header with title, badges, and action buttons */}
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <h4 className={`text-sm font-semibold truncate ${
                        isPresetActive(preset) || isDefault ? theme.text.inverse : theme.text.primary
                      }`}>
                        {preset.name}
                      </h4>
                        <div className="flex items-center gap-1">
                          {isPresetActive(preset) && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400">
                              Active
                            </span>
                          )}
                          {isDefault && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400">
                              Default
                            </span>
                          )}
                        </div>
                      </div>
                    {/* Action buttons moved to top right */}
                    <div className={cn(
                      "flex items-center ml-2 flex-shrink-0",
                      spacing,
                      actionsVisibility === 'hover' ? 'opacity-0 group-hover:opacity-100 transition-opacity duration-200' : 'opacity-100'
                    )}>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleApplyPreset(preset)}
                      className={`h-6 px-2 text-xs flex items-center gap-1 ${
                        isPresetActive(preset) || isDefault 
                          ? 'text-white/80 hover:text-white hover:bg-white/20' 
                          : `${theme.text.primary} hover:${theme.colors.backgroundHover}`
                      }`}
                      disabled={isPresetActive(preset)}
                    >
                      <CheckIcon className="w-3 h-3" />
                      Apply
                    </Button>
                    {!isDefault ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleSetAsDefault(preset.id)}
                        className={`h-6 px-2 text-xs ${
                          isPresetActive(preset) || isDefault
                            ? 'text-yellow-200 hover:text-yellow-100 hover:bg-white/20'
                            : 'text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300'
                        }`}
                        title="Set as default preset"
                      >
                        ⭐
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleRemoveDefault}
                        className={`h-6 px-2 text-xs ${
                          isPresetActive(preset) || isDefault
                            ? 'text-yellow-200 hover:text-yellow-100 hover:bg-white/20'
                            : 'text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-300'
                        }`}
                        title="Remove as default"
                      >
                        ⭐
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => startEditPreset(preset)}
                      className={`h-6 px-2 text-xs flex items-center gap-1 ${
                        isPresetActive(preset) || isDefault 
                          ? 'text-white/80 hover:text-white hover:bg-white/20' 
                          : `${theme.text.primary} hover:${theme.colors.backgroundHover}`
                      }`}
                    >
                      <PencilIcon className="w-3 h-3" />
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeletePreset(preset)}
                      className={`h-6 px-1.5 text-xs ${
                        isPresetActive(preset) || isDefault
                          ? 'text-red-300 hover:text-red-100 hover:bg-red-500/20'
                          : 'text-red-600 hover:text-red-800 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/30'
                      }`}
                      title="Delete preset"
                    >
                      <TrashIcon className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
                  
                  {/* Description - full width */}
                  {preset.description && (
                    <div className={`text-xs mb-2 ${
                      isPresetActive(preset) || isDefault ? 'text-white/80' : theme.text.secondary
                    }`}>
                      {preset.description}
                    </div>
                  )}
                  
                  {/* Summary info - full width */}
                  <div className={`text-xs flex items-center ${
                    isPresetActive(preset) || isDefault ? 'text-white/70' : theme.text.muted
                  }`}>
                    <DocumentTextIcon className="w-3 h-3 mr-1" />
                    {getConfigSummary(preset.config)} • {new Date(preset.createdAt).toLocaleDateString()}
                  </div>
              </div>
            );
          })
          )}
        </div>

            {/* Presets Instructions */}
            <div className="text-xs text-gray-500 dark:text-gray-400 border-t pt-3">
              <p>• Presets save table layout customizations</p>
              <p>• Includes columns, sizing, order, density & sorting</p>
              <p>• Data filters are separate and not saved in presets</p>
              <p>• Click ⭐ to set a preset as default</p>
              <p>• Default presets load automatically when form opens</p>
            </div>
          </div>
        )}
      </div>

      {/* Save Preset Modal */}
      <Modal isOpen={showSaveModal} onClose={() => {
        setShowSaveModal(false);
        setEditingPreset(null);
        setPresetName('');
        setPresetDescription('');
      }}>
        <ModalHeader>
          <ModalTitle>
            {editingPreset ? 'Update Table Preset' : 'Save Table Preset'}
          </ModalTitle>
        </ModalHeader>
        <ModalContent>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Preset Name</label>
              <Input
                value={presetName}
                onChange={(e) => setPresetName(e.target.value)}
                placeholder="e.g., Active Containers View"
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Description (Optional)</label>
              <Input
                value={presetDescription}
                onChange={(e) => setPresetDescription(e.target.value)}
                placeholder="e.g., Filtered view with custom columns for daily operations"
                className="w-full"
              />
            </div>
            <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div className="text-sm font-medium mb-2">What will be saved:</div>
              <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                <div className="font-medium text-gray-700 dark:text-gray-300">
                  {getConfigSummary(getCurrentConfig())}
                </div>
                <div className="text-xs mt-2 space-y-1">
                  <div>✓ Table density: {tableDensity}</div>
                  <div>✓ Column visibility: {Object.values(visibleColumns).filter(Boolean).length} of {Object.keys(visibleColumns).length} columns shown</div>
                  {columnOrder && columnOrder.length > 0 && (
                    <div>✓ Column order: Custom arrangement</div>
                  )}
                  {sortConfig && sortConfig.length > 0 && (
                    <div>✓ Sorting: {sortConfig.length} active sort{sortConfig.length !== 1 ? 's' : ''}</div>
                  )}
                  {columnWidths && Object.keys(columnWidths).length > 0 && (
                    <div>✓ Column sizing: {Object.keys(columnWidths).length} custom width{Object.keys(columnWidths).length !== 1 ? 's' : ''}</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </ModalContent>
        <ModalFooter className="flex justify-end space-x-3">
          <Button
            variant="outline"
            onClick={() => {
              setShowSaveModal(false);
              setEditingPreset(null);
              setPresetName('');
              setPresetDescription('');
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={editingPreset ? handleUpdatePreset : handleSavePreset}
            disabled={!presetName.trim()}
          >
            {editingPreset ? 'Update' : 'Save'} Preset
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showDeleteConfirm}
        onClose={cancelDelete}
        onConfirm={confirmDeletePreset}
        title="Delete Preset"
        message={`Are you sure you want to delete the preset "${presetToDelete?.name}"?`}
        details={presetToDelete ? {
          'Name': presetToDelete.name,
          ...(presetToDelete.description && { 'Description': presetToDelete.description }),
          'Configuration': getConfigSummary(presetToDelete.config)
        } : null}
        warningMessage={`This action cannot be undone.${getDefaultPresetId() === presetToDelete?.id ? ' ⚠️ This is your default preset. Deleting it will remove the default setting.' : ''}`}
        confirmText="Delete Preset"
        cancelText="Cancel"
        confirmVariant="destructive"
      />
    </div>
  );
};

export { TablePresets };
